import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  ParticipationRequestStatus,
  type BuyerParticipationRequestDto,
  type SourcingEventV2Dto,
  useExtendDpsAssessmentWindowMutation,
  useListParticipationRequestsForBuyerQuery,
  useRemoveFromDpsSchemeMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { participationStatusBadge } from "../../../../common/qualification/participationStatusBadge";
import { urls } from "../../../../urls";

interface Props {
  event: SourcingEventV2Dto;
  schemeOpen: boolean;
}

// The ordning's supplier register (FOA § 26-6): who is admitted and since when, alongside the
// applications still being assessed and the memberships that ended. It is the participation-request
// list — the register is a reading of it, not a second store.
export const DpsMembersTab = ({ event, schemeOpen }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useListParticipationRequestsForBuyerQuery({ eventId: event.id });
  const [removing, setRemoving] = useState<BuyerParticipationRequestDto | null>(null);
  const [extending, setExtending] = useState<BuyerParticipationRequestDto | null>(null);

  if (isLoading || !data) return null;

  const requests = data.requests ?? [];
  const qualified = requests.filter((r) => r.status === ParticipationRequestStatus.Qualified);
  const awaiting = requests.filter((r) => r.status === ParticipationRequestStatus.Submitted);

  if (requests.length === 0) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiUsers} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("No suppliers have applied yet")}
            </Text>
            <Text fontSize="sm" color="smMuted" maxW="md">
              {t("Suppliers can apply for as long as the system runs, and each application is assessed on its own.")}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Stack spacing="5">
      <Text fontSize="sm" color="smMuted">
        {awaiting.length > 0
          ? t("{{count}} qualified. {{awaiting}} awaiting assessment.", {
              count: qualified.length,
              awaiting: awaiting.length,
            })
          : t("{{count}} qualified suppliers are invited to every new competition.", { count: qualified.length })}
      </Text>

      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
        <Table size="md">
          <Thead>
            <Tr>
              <Th>{t("Supplier")}</Th>
              <Th>{t("Status")}</Th>
              <Th>{t("Qualified since")}</Th>
              <Th>{t("Assessment deadline")}</Th>
              <Th aria-hidden />
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((request) => (
              <MemberRow
                key={request.id}
                event={event}
                request={request}
                schemeOpen={schemeOpen}
                onRemove={() => setRemoving(request)}
                onExtend={() => setExtending(request)}
              />
            ))}
          </Tbody>
        </Table>
      </Box>

      {removing && (
        <RemoveMemberModal eventId={event.id} request={removing} onClose={() => setRemoving(null)} />
      )}
      {extending && (
        <ExtendAssessmentModal eventId={event.id} request={extending} onClose={() => setExtending(null)} />
      )}
    </Stack>
  );
};

const MemberRow = ({
  event,
  request,
  schemeOpen,
  onRemove,
  onExtend,
}: {
  event: SourcingEventV2Dto;
  request: BuyerParticipationRequestDto;
  schemeOpen: boolean;
  onRemove: () => void;
  onExtend: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const badge = participationStatusBadge(request.status);
  const awaiting = request.status === ParticipationRequestStatus.Submitted;

  const openRequest = () =>
    navigate(urls.v2.sourcingEvents.participationRequestDetail.go(event.id, request.id));

  return (
    <Tr _hover={{ bg: "smBackgroundSecondary" }} cursor="pointer" onClick={openRequest}>
      <Td>
        <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
          {request.organizationName ?? request.organizationId}
        </Text>
        {request.organizationNumber && (
          <Text fontSize="xs" color="smMuted" fontFamily="mono">
            {request.organizationNumber}
          </Text>
        )}
      </Td>
      <Td>
        <HStack spacing="2" flexWrap="wrap">
          <Badge colorScheme={badge.colorScheme} fontSize="xs">
            {t(badge.labelKey)}
          </Badge>
          {request.membershipEndReason && (
            <Text fontSize="xs" color="smMuted" noOfLines={1} maxW="72">
              {request.membershipEndReason}
            </Text>
          )}
        </HStack>
      </Td>
      <Td fontSize="sm" color="smMuted" whiteSpace="nowrap">
        {request.status === ParticipationRequestStatus.Qualified && request.qualificationVerdict
          ? displayDate(request.qualificationVerdict.decidedAt)
          : "–"}
      </Td>
      <Td whiteSpace="nowrap">
        {awaiting ? <AssessmentCountdown request={request} /> : <Text fontSize="sm" color="smMuted">–</Text>}
      </Td>
      {/* The row opens the application; the buttons act on it in place, so they keep their click
          to themselves. */}
      <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
        <HStack spacing="1" justify="flex-end">
          {/* Assessing is what an awaiting application is waiting for, so it is named rather than
              left to the chevron. */}
          {awaiting && (
            <Button size="sm" colorScheme="teal" onClick={openRequest}>
              {t("Assess")}
            </Button>
          )}
          {/* Names its object: every other action in the row acts on the supplier, so a bare
              "Utvid" reads as extending the membership rather than the clock next to it. */}
          {awaiting && !request.assessmentExtended && schemeOpen && (
            <Button size="sm" variant="ghost" onClick={onExtend}>
              {t("Extend deadline")}
            </Button>
          )}
          {request.status === ParticipationRequestStatus.Qualified && schemeOpen && (
            <Button size="sm" variant="ghost" colorScheme="red" onClick={onRemove}>
              {t("Remove")}
            </Button>
          )}
          <Icon as={FiChevronRight} color="smMuted" />
        </HStack>
      </Td>
    </Tr>
  );
};

// § 26-6: ten working days from receipt, fifteen once extended. Overdue is stated plainly rather
// than hidden — the buyer is already late and the number is what tells them.
const AssessmentCountdown = ({ request }: { request: BuyerParticipationRequestDto }) => {
  const { t } = useTranslation();
  const remaining = request.assessmentWorkingDaysRemaining;
  if (remaining == null) return <Text fontSize="sm" color="smMuted">–</Text>;

  const overdue = remaining < 0;
  return (
    <HStack spacing="2">
      <Text fontSize="sm" color={overdue ? "smError" : remaining <= 2 ? "smWarningText" : "smText"}>
        {overdue
          ? t("{{count}} working days overdue", { count: Math.abs(remaining) })
          : t("{{count}} working days left", { count: remaining })}
      </Text>
      {request.assessmentExtended && (
        <Badge colorScheme="purple" fontSize="xs">
          {t("Extended")}
        </Badge>
      )}
    </HStack>
  );
};

const RemoveMemberModal = ({
  eventId,
  request,
  onClose,
}: {
  eventId: string;
  request: BuyerParticipationRequestDto;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [reason, setReason] = useState("");
  const [remove, { isLoading }] = useRemoveFromDpsSchemeMutation();

  const submit = async () => {
    try {
      await remove({
        eventId,
        requestId: request.id,
        removeFromSchemeRequestDto: { reason },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <ReasonModal
      title={t("Remove from the system")}
      description={t(
        "{{supplier}} stops being invited to new competitions. Competitions already open keep the suppliers they were opened with.",
        { supplier: request.organizationName ?? request.organizationId },
      )}
      placeholder={t("Reason — sent to the supplier") ?? undefined}
      confirmLabel={t("Remove")}
      confirmColorScheme="red"
      reason={reason}
      onReasonChange={setReason}
      isLoading={isLoading}
      onConfirm={submit}
      onClose={onClose}
    />
  );
};

const ExtendAssessmentModal = ({
  eventId,
  request,
  onClose,
}: {
  eventId: string;
  request: BuyerParticipationRequestDto;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [reason, setReason] = useState("");
  const [extend, { isLoading }] = useExtendDpsAssessmentWindowMutation();

  const submit = async () => {
    try {
      await extend({
        eventId,
        requestId: request.id,
        extendAssessmentRequestDto: { reason },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <ReasonModal
      title={t("Extend the assessment window")}
      description={t("The deadline moves from 10 to 15 working days from receipt (FOA § 26-6). Allowed once.")}
      placeholder={t("Why the assessment needs longer") ?? undefined}
      confirmLabel={t("Extend")}
      confirmColorScheme="teal"
      reason={reason}
      onReasonChange={setReason}
      isLoading={isLoading}
      onConfirm={submit}
      onClose={onClose}
    />
  );
};

const ReasonModal = ({
  title,
  description,
  placeholder,
  confirmLabel,
  confirmColorScheme,
  reason,
  onReasonChange,
  isLoading,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  placeholder?: string;
  confirmLabel: string;
  confirmColorScheme: string;
  reason: string;
  onReasonChange: (value: string) => void;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="smMuted" mb="3">
            {description}
          </Text>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={placeholder}
            rows={4}
            autoFocus
          />
        </ModalBody>
        <ModalFooter gap="2" justifyContent="flex-start" flexDirection="row-reverse">
          <Button
            colorScheme={confirmColorScheme}
            isDisabled={reason.trim().length === 0}
            isLoading={isLoading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
